/**
 * Geography constants and helpers.
 * Extracted from WanderAppComplete.jsx for shared use by web and mobile.
 *
 * Functions that originally used closure-scoped caches now accept
 * an explicit `dynamicCache` parameter. Web wraps them with its
 * local caches; mobile can pass empty objects or its own caches.
 */

import { CITY_TO_COUNTRY } from './itineraryConstants.js';

// ─── Continent Mapping ──────────────────────────────────────────────────────

export const COUNTRY_TO_CONTINENT = {
  'usa': 'North America', 'united states': 'North America', 'united states of america': 'North America',
  'canada': 'North America', 'mexico': 'North America',
  'guatemala': 'North America', 'belize': 'North America', 'honduras': 'North America',
  'costa rica': 'North America', 'panama': 'North America', 'cuba': 'North America',
  'jamaica': 'North America', 'bahamas': 'North America', 'dominican republic': 'North America',
  'trinidad and tobago': 'North America', 'barbados': 'North America',
  'argentina': 'South America', 'brazil': 'South America', 'chile': 'South America',
  'colombia': 'South America', 'peru': 'South America', 'ecuador': 'South America',
  'uruguay': 'South America', 'venezuela': 'South America', 'bolivia': 'South America',
  'paraguay': 'South America',
  'uk': 'Europe', 'france': 'Europe', 'germany': 'Europe', 'italy': 'Europe',
  'spain': 'Europe', 'portugal': 'Europe', 'netherlands': 'Europe', 'belgium': 'Europe',
  'switzerland': 'Europe', 'austria': 'Europe', 'greece': 'Europe', 'turkey': 'Europe',
  'sweden': 'Europe', 'norway': 'Europe', 'denmark': 'Europe', 'finland': 'Europe',
  'ireland': 'Europe', 'iceland': 'Europe', 'czech republic': 'Europe', 'hungary': 'Europe',
  'poland': 'Europe', 'croatia': 'Europe', 'romania': 'Europe', 'montenegro': 'Europe',
  'slovenia': 'Europe', 'slovakia': 'Europe', 'serbia': 'Europe', 'albania': 'Europe',
  'north macedonia': 'Europe', 'bosnia and herzegovina': 'Europe', 'bulgaria': 'Europe',
  'estonia': 'Europe', 'latvia': 'Europe', 'lithuania': 'Europe', 'malta': 'Europe',
  'luxembourg': 'Europe', 'monaco': 'Europe', 'cyprus': 'Europe',
  'japan': 'Asia', 'china': 'Asia', 'south korea': 'Asia', 'taiwan': 'Asia',
  'thailand': 'Asia', 'vietnam': 'Asia', 'indonesia': 'Asia', 'malaysia': 'Asia',
  'singapore': 'Asia', 'philippines': 'Asia', 'cambodia': 'Asia', 'laos': 'Asia',
  'myanmar': 'Asia', 'india': 'Asia', 'sri lanka': 'Asia', 'nepal': 'Asia',
  'bhutan': 'Asia', 'maldives': 'Asia', 'uae': 'Asia', 'oman': 'Asia',
  'qatar': 'Asia', 'saudi arabia': 'Asia', 'jordan': 'Asia', 'israel': 'Asia',
  'lebanon': 'Asia', 'iran': 'Asia', 'iraq': 'Asia', 'syria': 'Asia',
  'georgia': 'Asia', 'armenia': 'Asia', 'azerbaijan': 'Asia',
  'mongolia': 'Asia', 'uzbekistan': 'Asia', 'kazakhstan': 'Asia', 'brunei': 'Asia',
  'bahrain': 'Asia', 'kuwait': 'Asia', 'yemen': 'Asia', 'afghanistan': 'Asia',
  'united arab emirates': 'Asia', 'turkmenistan': 'Asia', 'tajikistan': 'Asia', 'kyrgyzstan': 'Asia',
  'australia': 'Oceania', 'new zealand': 'Oceania', 'fiji': 'Oceania',
  'papua new guinea': 'Oceania', 'samoa': 'Oceania', 'tonga': 'Oceania',
  'french polynesia': 'Oceania', 'new caledonia': 'Oceania', 'palau': 'Oceania',
  'south africa': 'Africa', 'morocco': 'Africa', 'egypt': 'Africa', 'kenya': 'Africa',
  'tanzania': 'Africa', 'ethiopia': 'Africa', 'namibia': 'Africa', 'botswana': 'Africa',
  'zimbabwe': 'Africa', 'zambia': 'Africa', 'mozambique': 'Africa', 'madagascar': 'Africa',
  'mauritius': 'Africa', 'seychelles': 'Africa', 'rwanda': 'Africa', 'uganda': 'Africa',
  'ghana': 'Africa', 'nigeria': 'Africa', 'senegal': 'Africa', 'tunisia': 'Africa',
  'cape verde': 'Africa', 'comoros': 'Africa',
  'russia': 'Europe',
  // Antarctica — treated as its own continent so the intercity resolver
  // recognizes it and doesn't fall back to 1.5h heuristic flights
  'antarctica': 'Antarctica',
};

// ─── Island Location Keywords ───────────────────────────────────────────────

export const ISLAND_LOCATION_KEYWORDS = [
  'faroe', 'tórshavn', 'torshavn', 'vágar', 'streymoy', 'eysturoy',  // Faroe Islands
  'hawaii', 'maui', 'oahu', 'kauai', 'honolulu', 'kona', 'hilo',    // Hawaii
  'bali', 'lombok', 'nusa',                                           // Indonesian islands
  'phuket', 'koh samui', 'ko samui', 'krabi',                         // Thai islands
  'santorini', 'mykonos', 'crete', 'rhodes', 'corfu',                 // Greek islands
  'mallorca', 'ibiza', 'menorca', 'tenerife', 'gran canaria', 'lanzarote', 'fuerteventura', // Spanish islands
  'sicily', 'sardinia', 'capri',                                       // Italian islands
  'corsica',                                                            // French islands
  'maldives', 'male',                                                   // Maldives
  'bermuda', 'bahamas', 'nassau', 'jamaica', 'kingston',               // Caribbean
  'fiji', 'suva', 'nadi',                                               // Fiji
  'bora bora', 'tahiti', 'moorea',                                      // French Polynesia
  'zanzibar', 'seychelles', 'mauritius',                                // Indian Ocean islands
  'okinawa',                                                            // Japanese islands (Hokkaido excluded — has train tunnel)
  'isle of man', 'jersey', 'guernsey',                                  // British islands
  'madeira', 'azores',                                                  // Portuguese islands
  'puerto rico', 'guam', 'us virgin islands', 'american samoa',        // US territories (islands)
  'réunion', 'reunion', 'new caledonia', 'martinique', 'guadeloupe',   // French overseas territories
  'aruba', 'curacao', 'curaçao', 'bonaire',                            // Dutch Caribbean
  'palawan', 'boracay', 'cebu',                                        // Philippine islands
  'langkawi', 'penang',                                                 // Malaysian islands
  'jeju',                                                               // South Korean island
  'tasmania',                                                           // Australian island
  'isle of skye', 'isle of wight',                                      // UK islands
  'koh phi phi', 'koh lanta', 'koh tao', 'koh chang',                  // More Thai islands
  'comoros', 'cape verde', 'são tomé',                                  // African island nations
  'galapagos', 'easter island', 'isla de pascua',                       // Pacific islands
  'svalbard',                                                           // Arctic Norway
  'iceland', 'reykjavik', 'reykjavík', 'akureyri', 'keflavik', 'keflavík', // Iceland (island nation)
  'malta', 'valletta', 'gozo',                                             // Malta (island nation)
  'cyprus', 'nicosia', 'limassol', 'paphos', 'larnaca',                   // Cyprus (island)
  'sri lanka', 'colombo', 'kandy', 'galle',                               // Sri Lanka (island)
  'cuba', 'havana',                                                        // Cuba
  'dominican republic', 'santo domingo', 'punta cana',                     // Dominican Republic
  'trinidad', 'tobago',                                                    // Trinidad & Tobago
];

// ─── Helper Functions ───────────────────────────────────────────────────────

/**
 * Check if a city name matches a known island location.
 * Checks: 1) static keyword list, 2) dynamic geography cache from Haiku
 *
 * @param {string} cityName
 * @param {Object} [dynamicCache={}] - Web passes its dynamicGeographyCache; mobile can pass {}
 * @returns {boolean}
 */
export function isIslandCity(cityName, dynamicCache = {}) {
  if (!cityName) return false;
  const lower = cityName.toLowerCase().trim();
  // Check static keyword list
  if (lower.includes('island') || ISLAND_LOCATION_KEYWORDS.some(kw => lower.includes(kw))) return true;
  // Check dynamic geography cache (from getDestinationGeography Haiku results)
  const geoData = dynamicCache[lower];
  if (geoData?.isIsland || geoData?.isArchipelago || geoData?.noCarAccess) return true;
  return false;
}

/**
 * Get continent for a country name.
 *
 * @param {string} country
 * @param {Object} [dynamicCache={}] - Web passes its dynamicCountryContinentCache
 * @returns {string|null}
 */
export function getContinentForCountry(country, dynamicCache = {}) {
  if (!country) return null;
  const countryLower = country.toLowerCase().trim();
  return COUNTRY_TO_CONTINENT[countryLower] || dynamicCache[countryLower] || null;
}

/**
 * Synchronous city→country lookup using static table + optional dynamic cache.
 *
 * @param {string} cityName
 * @param {Object} [dynamicCache={}] - Web passes its dynamicCityCountryCache
 * @returns {string|null}
 */
/**
 * Infer continent from a text string by checking existing data sources dynamically.
 * Uses: COUNTRY_TO_CONTINENT (countries), CITY_TO_COUNTRY → COUNTRY_TO_CONTINENT (cities),
 * and optional dynamicCache from Haiku lookups.
 *
 * No hardcoded city/region lists — relies entirely on the existing mappings.
 *
 * @param {string} text
 * @param {Object} [dynamicCache={}] - Web passes its dynamicGeographyCache or dynamicCityCountryCache
 * @returns {string|null} e.g., 'Europe', 'Asia', 'North America', etc.
 */
export function getContinentHintFromText(text, dynamicCache = {}) {
  if (!text) return null;
  const t = text.toLowerCase().trim();

  // Collect text fragments to check: full text + parts after comma (for "City, Country")
  const textsToCheck = [t];
  if (t.includes(',')) {
    t.split(',').forEach(part => {
      const trimmed = part.trim();
      if (trimmed) textsToCheck.push(trimmed);
    });
  }

  for (const checkText of textsToCheck) {
    // 1. Direct country match in COUNTRY_TO_CONTINENT
    if (COUNTRY_TO_CONTINENT[checkText]) return COUNTRY_TO_CONTINENT[checkText];

    // 2. City → country → continent via CITY_TO_COUNTRY
    const country = CITY_TO_COUNTRY[checkText];
    if (country) {
      const continent = COUNTRY_TO_CONTINENT[country.toLowerCase()];
      if (continent) return continent;
    }

    // 3. Dynamic cache — from Haiku geography lookups at runtime
    const dynCountry = dynamicCache[checkText];
    if (dynCountry) {
      // dynCountry might be a string (country name) or an object with .country
      const countryName = typeof dynCountry === 'string' ? dynCountry : dynCountry?.country;
      if (countryName) {
        const continent = COUNTRY_TO_CONTINENT[countryName.toLowerCase()];
        if (continent) return continent;
      }
    }

    // 4. Partial match — check if text contains a known country name
    //    (handles regions like "Patagonia, Argentina" or "Tuscany, Italy" after splitting)
    for (const [countryKey, continent] of Object.entries(COUNTRY_TO_CONTINENT)) {
      if (checkText === countryKey || (checkText.length > 3 && checkText.includes(countryKey))) {
        return continent;
      }
    }
  }

  return null;
}

// Common country name aliases → canonical form used in CITY_TO_COUNTRY
// US states → "USA" so "Canyon Point, Utah" resolves correctly
const US_STATES = [
  'alabama', 'alaska', 'arizona', 'arkansas', 'california', 'colorado', 'connecticut',
  'delaware', 'florida', 'georgia', 'hawaii', 'idaho', 'illinois', 'indiana', 'iowa',
  'kansas', 'kentucky', 'louisiana', 'maine', 'maryland', 'massachusetts', 'michigan',
  'minnesota', 'mississippi', 'missouri', 'montana', 'nebraska', 'nevada', 'new hampshire',
  'new jersey', 'new mexico', 'new york', 'north carolina', 'north dakota', 'ohio',
  'oklahoma', 'oregon', 'pennsylvania', 'rhode island', 'south carolina', 'south dakota',
  'tennessee', 'texas', 'utah', 'vermont', 'virginia', 'washington', 'west virginia',
  'wisconsin', 'wyoming',
];
const US_STATE_MAP = Object.fromEntries(US_STATES.map(s => [s, 'USA']));

const COUNTRY_ALIASES = {
  'united states': 'USA', 'united states of america': 'USA', 'us': 'USA', 'u.s.': 'USA', 'u.s.a.': 'USA',
  'united kingdom': 'UK', 'great britain': 'UK', 'england': 'UK', 'scotland': 'UK', 'wales': 'UK',
  'south korea': 'South Korea', 'republic of korea': 'South Korea',
  'uae': 'UAE', 'united arab emirates': 'UAE',
  'czech republic': 'Czech Republic', 'czechia': 'Czech Republic',
  'new zealand': 'New Zealand', 'nz': 'New Zealand',
  ...US_STATE_MAP,
};

export function getCityCountrySync(cityName, dynamicCache = {}) {
  if (!cityName) return null;
  const cityLower = cityName.toLowerCase().trim();
  // Direct lookup first
  const direct = CITY_TO_COUNTRY[cityLower] || dynamicCache[cityLower];
  if (direct) return direct;
  // Handle "City, Country" format (e.g., "Lyon, France" or "Hawley, United States")
  if (cityLower.includes(',')) {
    const parts = cityLower.split(',').map(p => p.trim());
    const cityOnly = parts[0];
    // Check city part in lookup tables
    const cityResult = CITY_TO_COUNTRY[cityOnly] || dynamicCache[cityOnly];
    if (cityResult) return cityResult;
    // Check country part after comma — handles "Hawley, United States" where city isn't in table
    if (parts.length > 1) {
      const countryPart = parts[parts.length - 1];
      // Direct match in aliases
      const aliased = COUNTRY_ALIASES[countryPart];
      if (aliased) return aliased;
      // Capitalize and return if it looks like a country name (found in COUNTRY_TO_CONTINENT)
      if (COUNTRY_TO_CONTINENT[countryPart]) {
        // Return a canonical country name (title case)
        return countryPart.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      }
      // Also check dynamic cache for the country part
      const dynCountry = dynamicCache[countryPart];
      if (dynCountry) return typeof dynCountry === 'string' ? dynCountry : dynCountry?.country || null;
    }
    return null;
  }
  // Check if the whole string is a known country alias
  const aliased = COUNTRY_ALIASES[cityLower];
  if (aliased) return aliased;
  return null;
}
